// frontend/src/widgets/WidgetRegistry.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-25
// Updated:      2026-10-28 11:02 GMT
// Change Summary (2026-10-28 11:02 GMT):
//   - Exposed Vendor Pricebooks Admin on Clients/Customers surface as well as System.
// Change Summary (2026-10-28 10:08 UTC):
//   - Registered dedicated Vendor Pricebooks Admin on System surface. Sales Pricebooks untouched.
//
// Description:  Canonical widget catalogue for component registration, metadata, surface availability,
//               layout defaults, picker filtering, rendering lookup, and database seed synchronisation.
//
//
// Change Summary (2026-10-27 GMT):
//   - Registered Reusable Quote Notes management on Quotes surface (ID quote_note_snippets).
// Change Summary (2026-10-08 15:24 BST):
//   - Registered Location Intelligence Overview for the Monitor surface.
// Change Summary (2026-10-08 09:31 BST):
//   - Removed the Job Form-specific registry layout override added with Work Packages.
//   - Restored Job Form registry geometry to exact pre-Work-Package/Git behaviour.
// Change Summary (2026-10-07 15:42 BST):
//   - Restored explicit Job Form desktop grid geometry after runtime frontend integration.
//
// Change Summary (2026-10-05):
//   - Registered Work Package Taxonomy Admin as a separate Jobs-surface widget.
//   - Job Taxonomy Admin remains unchanged and independently registered.
//
// Change Summary (2026-07-30):
//   - Added stable registry query helpers used by the shared widget framework.
//   - Added non-mutating surface filtering and canonical widget lookup.
//   - Added shared layout-default resolution without requiring every legacy entry to be rewritten.
//   - Registered the Clients surface against every widget currently renderable by ClientsPage.
//   - Retained markWidgetUsedIn temporarily for legacy pages, but new pages must not depend on it.
//
// Operational Notes:
//   - Widget ids are durable contracts shared by the registry, DB `widgets.name`, and stored layouts.
//   - `dashboards` is the presentation-surface allow-list used by Add Widget.
//   - New or migrated pages must use getWidgetsForSurface/getWidgetById rather than filtering directly.
//   - Runtime mutation through markWidgetUsedIn is transitional technical debt and will be removed
//     after all widget-enabled pages have migrated to the shared framework.
//   - Uses the shared Axios instance for registry/database synchronisation.

import SystemStatusWidget from "./SystemStatusWidget";
import AdvancedUserListWidget from "./advanced_user_list";
import RoleStatsWidget from "./RoleStatsWidget";
import AlertWidget from "./alert_widget";
import UserFormWidget from "./UserFormWidget";
import UserListWidget from "./UserListWidget";
import LoggedInUsersWidget from "./LoggedInUsersWidget";
import StandardReportsWidget from "./StandardReportsWidget";
import DynamicReportBuilder from "./DynamicReportBuilder";
import MetricGraphWidget from "./MetricGraphWidget";
import MetricGaugeWidget from "./MetricGaugeWidget";
import MetricGaugePanelWidget from "./MetricGaugePanelWidget";
import JobFormWidget from "./JobFormWidget";
import JobFileUploadWidget from "./JobFileUploadWidget";
import JobListWidget from "./JobListWidget";
import JobProgressWidget from "./JobProgressWidget";
import AdvancedTaskListWidget from "./AdvancedTaskListWidget.jsx";
import MyTasksWidget from "./MyTasksWidget.jsx";
import WorkPackageProgressWidget from "./WorkPackageProgressWidget.jsx";
import CustomerFormWidget from "./CustomerFormWidget";
import ContactFormWidget from "./ContactFormWidget";
import AdvancedCustomerListWidget from "./AdvancedCustomerListWidget";
import AdvancedContactListWidget from "./AdvancedContactListWidget";
import TaxonomyAdminWidget from "./TaxonomyAdminWidget";
import WorkPackageTaxonomyAdminWidget from "./WorkPackageTaxonomyAdminWidget";

import DocumentLibraryWidget from "./DocumentLibraryWidget.jsx";
import OrgCalendarWidget from "./OrgCalendarWidget.jsx";
import ResourceExpensesWidget from "./ResourceExpensesWidget.jsx";
import ResourceFormWidget from "./ResourceFormWidget.jsx";
import ResourceListWidget from "./ResourceListWidget.jsx";
import ResourceRolesWidget from "./ResourceRolesWidget.jsx";

import MessagingTemplateLibraryWidget from "./MessagingTemplateLibraryWidget.jsx";
import OpenConversationsWidget from "./OpenConversationsWidget.jsx";
import MessagesAttentionWidget from "./MessagesAttentionWidget.jsx";
import MessagingHealthWidget from "./MessagingHealthWidget.jsx";

import JobCreateDrawer from "./JobCreateDrawer";

import OpportunityStageProgressWidget from "./OpportunityStageProgressWidget.jsx";
import OpportunityInboxWidget from "./OpportunityInboxWidget.jsx";
import OpportunityListWidget from "./OpportunityListWidget.jsx";
import OpportunityDetailWidget from "./OpportunityDetailWidget.jsx";
import ClientContactSummaryWidget from "./ClientContactSummaryWidget.jsx";
import OpportunityAIInsightsWidget from "./OpportunityAIInsightsWidget.jsx";
import OpportunityFinancialSummaryWidget from "./OpportunityFinancialSummaryWidget.jsx";

import TermsAndConditionsWidget from "./TermsAndConditionsWidget.jsx";
import QuoteNoteSnippetsWidget from "./QuoteNoteSnippetsWidget.jsx";
import PricebooksAdminWidget from "./PricebooksAdminWidget.jsx";
import VendorPricebooksAdminWidget from "./VendorPricebooksAdminWidget.jsx";
import AuthorityDirectoryWidget from "./AuthorityDirectoryWidget.jsx";
import WeatherSpotWidget from "./WeatherSpotWidget.jsx";
import FloodIntelligenceWidget from "./FloodIntelligenceWidget.jsx";
import TrafficIntelligenceWidget from "./TrafficIntelligenceWidget.jsx";
import WorksIntelligenceWidget from "./WorksIntelligenceWidget.jsx";
import LocationIntelligenceOverviewWidget from "./LocationIntelligenceOverviewWidget.jsx";

import CameraEnrollmentWidget from "./CameraEnrollmentWidget.jsx";
import CameraJobBindingWidget from "./CameraJobBindingWidget.jsx";
import CameraHeartbeatWidget from "./CameraHeartbeatWidget.jsx";
import CameraIngestActivityWidget from "./CameraIngestActivityWidget.jsx";
import WeavingReportWidget from "./WeavingReportWidget.jsx";
import ANPRSightingReviewWidget from "./ANPRSightingReviewWidget.jsx";

import axiosInstance from "../api/axiosInstance";

const WidgetRegistry = [
  // ——— System ———
  {
    id: "system_status",
    component: SystemStatusWidget,
    friendly_name: "System Status",
    description: "System health and status overview",
    category: "System",
    dashboards: ["dashboard", "system"],
  },
  {
    id: "alert_widget",
    component: AlertWidget,
    friendly_name: "System Alerts",
    description: "Live system alerts with severity highlighting",
    category: "System",
    dashboards: ["dashboard", "system"],
  },
  {
    id: "metric_graph_widget",
    component: MetricGraphWidget,
    friendly_name: "Metric Graph",
    description: "Historical graph for CPU, RAM, Disk, GPU with thresholds",
    category: "System",
    dashboards: ["dashboard", "system"],
  },
  {
    id: "metric_gauge_widget",
    component: MetricGaugeWidget,
    friendly_name: "Single Metric Gauge",
    description: "Animated circular gauge for a single metric (CPU, RAM, etc)",
    category: "System",
    dashboards: ["dashboard", "system"],
  },
  {
    id: "metric_gauge_panel",
    component: MetricGaugePanelWidget,
    friendly_name: "System Gauges",
    description: "Animated gauges for CPU, RAM, Disk, GPU",
    category: "System",
    dashboards: ["dashboard", "system"],
  },
  {
    id: "weather_spot_widget",
    component: WeatherSpotWidget,
    friendly_name: "Met Office Weather",
    description: "Met Office Global Spot forecast using pinned location (search + map pin).",
    category: "System",
    dashboards: ["dashboard", "system", "jobs"],
  },

  // ——— User Management ———
  {
    id: "advanced_user_list",
    component: AdvancedUserListWidget,
    friendly_name: "Advanced User List",
    description: "Extended user list with avatar URL display",
    category: "User Management",
    dashboards: ["dashboard", "users"],
  },
  {
    id: "role_stats",
    component: RoleStatsWidget,
    friendly_name: "Role Statistics",
    description: "Summary of roles and user counts",
    category: "User Management",
    dashboards: ["dashboard", "users"],
  },
  {
    id: "user_form",
    component: UserFormWidget,
    friendly_name: "User Form",
    description: "Form to add or edit users",
    category: "User Management",
    dashboards: ["dashboard", "users"],
  },
  {
    id: "user_list",
    component: UserListWidget,
    friendly_name: "User List",
    description: "List of users with roles",
    category: "User Management",
    dashboards: ["dashboard", "users"],
  },
  {
    id: "logged_in_users",
    component: LoggedInUsersWidget,
    friendly_name: "Logged-In Users",
    description: "Expandable widget showing currently logged-in users",
    category: "User Management",
    dashboards: ["dashboard", "users"],
  },

  // ——— Reporting ———
  {
    id: "standard_reports_widget",
    component: StandardReportsWidget,
    friendly_name: "Standard Reports",
    description: "Displays developer-created standard reports for export",
    category: "Reporting",
    dashboards: ["dashboard", "reporting"],
  },
  {
    id: "dynamic_report_builder",
    component: DynamicReportBuilder,
    friendly_name: "Dynamic Report Builder",
    description: "Create ad hoc reports with field selection and date range",
    category: "Reporting",
    dashboards: ["dashboard", "reporting"],
  },
  {
    id: "weaving_report_widget",
    component: WeavingReportWidget,
    friendly_name: "Weaving Report",
    description: "Route-based weaving, non-rejoin, and traversal analysis for paired ANPR cameras.",
    category: "Reporting",
    dashboards: ["cameras", "reporting"],
  },

  // ——— ANPR ———
  {
    id: "anpr_sighting_review",
    component: ANPRSightingReviewWidget,
    friendly_name: "ANPR Sighting Review",
    description: "Human review and audit workflow for suspect ANPR sightings.",
    category: "ANPR",
    dashboards: ["cameras", "reporting", "dashboard"],
  },

  // ——— Jobs ———
  {
    id: "job_form",
    component: JobFormWidget,
    friendly_name: "Job Form (Legacy)",
    description: "Create a new ANPR job with reference, model, and client.",
    category: "Jobs",
    dashboards: ["jobs"],
  },
  {
    id: "job_create",
    component: JobCreateDrawer,
    friendly_name: "Job Create (Drawer)",
    description: "Modern job creation with taxonomy, customer/contact and dates.",
    category: "Jobs",
    dashboards: ["jobs"],
  },
  {
    id: "job_upload",
    component: JobFileUploadWidget,
    friendly_name: "Upload Files",
    description: "Upload job files and extract EXIF metadata",
    category: "Jobs",
    dashboards: ["jobs"],
  },
  {
    id: "job_list",
    layout: { w: 6, h: 5, minW: 4, minH: 4, maxW: 12 },
    component: JobListWidget,
    friendly_name: "Job List",
    description: "Displays a table of ANPR jobs with status and metadata",
    category: "Jobs",
    dashboards: ["jobs", "tasks", "clients"],
  },
  {
    id: "advanced_task_list",
    layout: { w: 8, h: 7, minW: 5, minH: 5, maxW: 12 },
    component: AdvancedTaskListWidget,
    friendly_name: "Advanced Task List",
    description: "All runtime workflow tasks across active workflows.",
    category: "Tasks",
    dashboards: ["tasks", "jobs"],
  },
  {
    id: "my_tasks",
    layout: { w: 4, h: 7, minW: 3, minH: 5, maxW: 12 },
    component: MyTasksWidget,
    friendly_name: "My Tasks",
    description: "Runtime tasks assigned to the authenticated user's linked Resource.",
    category: "Tasks",
    dashboards: ["tasks", "jobs"],
  },
  {
    id: "work_package_progress",
    layout: { w: 6, h: 7, minW: 4, minH: 5, maxW: 12 },
    component: WorkPackageProgressWidget,
    friendly_name: "Work Package Progress",
    description: "Runtime Work Package workflow progress with task launchers.",
    category: "Tasks",
    dashboards: ["jobs", "tasks"],
  },
  {
    id: "job_progress",
    component: JobProgressWidget,
    friendly_name: "Job Progress",
    description: "Real-time progress tracker for ANPR job with current step.",
    category: "Jobs",
    dashboards: ["jobs", "dashboard"],
  },
  {
    id: "taxonomy_admin",
    component: TaxonomyAdminWidget,
    friendly_name: "Taxonomy Admin",
    description: "Manage Job Types, Subtypes, and Subtype2 inline on the Jobs page.",
    category: "Jobs",
    dashboards: ["jobs"],
  },
  {
    id: "work_package_taxonomy_admin",
    component: WorkPackageTaxonomyAdminWidget,
    friendly_name: "Work Package Taxonomy Admin",
    description: "Manage Work Package Types, Subtypes, and Subtype2 inline on the Jobs page.",
    category: "Jobs",
    dashboards: ["jobs"],
  },
  {
    id: "authority_directory",
    component: AuthorityDirectoryWidget,
    friendly_name: "Authority Directory",
    description: "Maintain licensing/highways authority directory (tenant-scoped) for admin boundary enrichment.",
    category: "Jobs",
    dashboards: ["jobs"],
  },
  {
    id: "location_intelligence_overview",
    layout: { w: 12, h: 16, minW: 6, minH: 10, maxW: 12 },
    component: LocationIntelligenceOverviewWidget,
    friendly_name: "Location Intelligence Overview",
    description: "Consolidated pinned-location weather, works, flood and traffic intelligence with interactive map.",
    category: "Operations",
    dashboards: ["monitor"],
  },
  {
    id: "flood_intelligence_widget",
    component: FloodIntelligenceWidget,
    friendly_name: "Flood Intelligence",
    description: "Readable flood-risk intelligence using pinned lat/lon, including alerts, water level, rainfall and tide summary.",
    category: "Jobs",
    dashboards: ["jobs"],
  },
  {
    id: "traffic_intelligence_widget",
    component: TrafficIntelligenceWidget,
    friendly_name: "Traffic Intelligence",
    description: "Readable live traffic and incident intelligence using pinned lat/lon, including operational impact, flow conditions and surfaced incidents.",
    category: "Jobs",
    dashboards: ["jobs"],
  },
  {
    id: "works_intelligence",
    component: WorksIntelligenceWidget,
    friendly_name: "Works Intelligence",
    description: "Readable Street Manager works and road restriction intelligence for a selected job, including risk band, nearest issue and surfaced disruptions.",
    category: "Jobs",
    dashboards: ["jobs", "operations"],
  },

  // ——— CRM ———
  {
    id: "customer_form",
    layout: { w: 6, h: 5, minW: 4, minH: 4, maxW: 12 },
    component: CustomerFormWidget,
    friendly_name: "Customer Form",
    description: "Form to create and manage customer organisations",
    category: "CRM",
    dashboards: ["clients"],
  },
  {
    id: "contact_form",
    layout: { w: 6, h: 5, minW: 4, minH: 4, maxW: 12 },
    component: ContactFormWidget,
    friendly_name: "Contact Form",
    description: "Create and manage contacts under a customer organisation",
    category: "CRM",
    dashboards: ["clients", "contacts"],
  },
  {
    id: "advanced_customer_list",
    layout: { w: 6, h: 5, minW: 4, minH: 4, maxW: 12 },
    component: AdvancedCustomerListWidget,
    friendly_name: "Customer List",
    description: "List of all customers with actions",
    category: "CRM",
    dashboards: ["clients"],
  },
  {
    id: "advanced_contact_list",
    layout: { w: 6, h: 5, minW: 4, minH: 4, maxW: 12 },
    component: AdvancedContactListWidget,
    friendly_name: "Contact List",
    description: "List of contacts with edit options",
    category: "CRM",
    dashboards: ["clients", "contacts"],
  },

  // ——— Messaging ———
  {
    id: "messaging_template_library",
    component: MessagingTemplateLibraryWidget,
    friendly_name: "Messaging Template Library",
    description: "Manage templates for Email, SMS and WhatsApp",
    category: "Messaging",
    dashboards: ["messaging"],
  },
  {
    id: "open_conversations",
    component: OpenConversationsWidget,
    friendly_name: "Open Conversations",
    description: "List of active conversations across channels",
    category: "Messaging",
    dashboards: ["messaging"],
  },
  {
    id: "messages_attention",
    component: MessagesAttentionWidget,
    friendly_name: "Messages Requiring Attention",
    description: "Items requiring follow-up",
    category: "Messaging",
    dashboards: ["messaging"],
  },
  {
    id: "messaging_health",
    component: MessagingHealthWidget,
    friendly_name: "Messaging Health",
    description: "Status for Email, SMS, WhatsApp channels",
    category: "Messaging",
    dashboards: ["messaging"],
  },

  // ——— Devices ———
  {
    id: "camera_enrollment",
    component: CameraEnrollmentWidget,
    friendly_name: "Camera Enrollment",
    description: "Register and manage AiQ camera devices for ingest onboarding.",
    category: "Devices",
    dashboards: ["cameras", "jobs", "dashboard"],
  },
  {
    id: "camera_job_binding",
    component: CameraJobBindingWidget,
    friendly_name: "Camera Job Binding",
    description: "Bind cameras to jobs and view active camera-to-job assignments.",
    category: "Devices",
    dashboards: ["cameras", "jobs", "dashboard"],
  },
  {
    id: "camera_heartbeat",
    component: CameraHeartbeatWidget,
    friendly_name: "Camera Heartbeat",
    description: "Show online, stale, offline and disabled camera heartbeat status using last ingest activity.",
    category: "Devices",
    dashboards: ["cameras", "jobs", "dashboard"],
  },
  {
    id: "camera_ingest_activity",
    component: CameraIngestActivityWidget,
    friendly_name: "Camera Ingest Activity",
    description: "Show recent ingest events, parse status, and inserted versus duplicate counts across devices.",
    category: "Devices",
    dashboards: ["cameras", "jobs", "dashboard"],
  },

  // ——— Documents / Admin / Other ———
  {
    id: "document_library",
    component: DocumentLibraryWidget,
    friendly_name: "Document Library",
    description: "Browse and open documents",
    category: "Documents",
    dashboards: ["documents"],
  },
  {
    id: "org_calendar",
    component: OrgCalendarWidget,
    friendly_name: "Organisational Calendar",
    description: "Organisation-wide calendar overview",
    category: "Operations",
    dashboards: ["operations"],
  },
  {
    id: "resource_expenses",
    component: ResourceExpensesWidget,
    friendly_name: "Resource Expenses",
    description: "Expenses by resource with approval workflow",
    category: "Operations",
    dashboards: ["operations"],
  },
  {
    id: "resource_form",
    component: ResourceFormWidget,
    friendly_name: "Resource Form",
    description: "Create or edit resources",
    category: "Operations",
    dashboards: ["operations"],
  },
  {
    id: "resource_list",
    component: ResourceListWidget,
    friendly_name: "Resource List",
    description: "List of resources (active/inactive)",
    category: "Operations",
    dashboards: ["operations"],
  },
  {
    id: "resource_roles",
    component: ResourceRolesWidget,
    friendly_name: "Resource Roles",
    description: "Manage resource roles and permissions",
    category: "Operations",
    dashboards: ["operations"],
  },

  // ——— Opportunities ———
  {
    id: "opportunity_stage_progress",
    component: OpportunityStageProgressWidget,
    friendly_name: "Stage Progress",
    description: "Stage and status controls for the selected opportunity",
    category: "Opportunities",
    dashboards: ["opportunities"],
  },
  {
    id: "opportunity_inbox",
    component: OpportunityInboxWidget,
    friendly_name: "Opportunity Inbox",
    description: "Flags and attention items across opportunities",
    category: "Opportunities",
    dashboards: ["opportunities"],
  },
  {
    id: "opportunity_list",
    component: OpportunityListWidget,
    friendly_name: "Opportunity List",
    description: "Selectable list of opportunities",
    category: "Opportunities",
    dashboards: ["opportunities"],
  },
  {
    id: "opportunity_detail",
    component: OpportunityDetailWidget,
    friendly_name: "Opportunity Details",
    description: "Detail view and editor for the selected opportunity",
    category: "Opportunities",
    dashboards: ["opportunities"],
  },
  {
    id: "client_contact_summary",
    component: ClientContactSummaryWidget,
    friendly_name: "Client & Contact Summary",
    description: "Customer + primary contact summary for the selected opportunity",
    category: "Opportunities",
    dashboards: ["opportunities"],
  },
  {
    id: "financial_summary_widget",
    component: OpportunityFinancialSummaryWidget,
    friendly_name: "Financial Summary",
    description: "Pipeline summary and breakdowns (stage/status/owner/region)",
    category: "Opportunities",
    dashboards: ["opportunities"],
  },
  {
    id: "opportunity_ai_insights",
    component: OpportunityAIInsightsWidget,
    friendly_name: "AI Insights",
    description: "AI-generated insights and next steps (advisory)",
    category: "Opportunities",
    dashboards: ["opportunities"],
  },

  // ——— Sales ———
  {
    id: "terms_and_conditions",
    component: TermsAndConditionsWidget,
    friendly_name: "Terms & Conditions",
    description: "Manage versioned T&Cs for Quotes",
    category: "Sales",
    dashboards: ["opportunities"],
  },
  {
    id: "quote_note_snippets",
    layout: { w: 8, h: 8, minW: 5, minH: 6, maxW: 12 },
    component: QuoteNoteSnippetsWidget,
    friendly_name: "Reusable Quote Notes",
    description: "Manage formatted reusable Specific Notes snippets for quotations.",
    category: "Sales",
    dashboards: ["quotes"],
  },
  {
    id: "vendor_pricebooks_admin",
    component: VendorPricebooksAdminWidget,
    friendly_name: "Vendor Pricebooks (Admin)",
    description: "Manage supplier procurement rate books and effective-dated cost history (independent of sales pricing).",
    category: "Operations",
    dashboards: ["system", "clients"],
  },
  {
    id: "pricebooks_admin",
    component: PricebooksAdminWidget,
    friendly_name: "Pricebooks (Admin)",
    description: "Create/edit/deactivate pricebooks (tenant / customer / resource scoped)",
    category: "Sales",
    dashboards: ["quotes"],
  },
];

const DEFAULT_WIDGET_LAYOUT = Object.freeze({
  w: 6,
  h: 4,
  minW: 3,
  minH: 3,
  maxW: 12,
});

export const getWidgetById = (widgetId) =>
  WidgetRegistry.find((widget) => widget.id === widgetId) || null;

export const getWidgetsForSurface = (surfaceName, options = {}) => {
  const { includeInactive = false } = options;

  return WidgetRegistry.filter((widget) => {
    const active = includeInactive || widget.is_active !== false;
    const allowed = Array.isArray(widget.dashboards) && widget.dashboards.includes(surfaceName);
    return active && allowed;
  });
};

export const isWidgetAllowedOnSurface = (widgetId, surfaceName) => {
  const widget = getWidgetById(widgetId);
  return Boolean(widget && Array.isArray(widget.dashboards) && widget.dashboards.includes(surfaceName));
};

export const getWidgetLayoutDefaults = (widgetId) => {
  const widget = getWidgetById(widgetId);
  return { ...DEFAULT_WIDGET_LAYOUT, ...(widget?.layout || {}) };
};

export const getWidgetTitle = (widgetId) => {
  const widget = getWidgetById(widgetId);
  return (
    widget?.friendly_name ||
    String(widgetId || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase())
  );
};

export const markWidgetUsedIn = (widgetId, dashboardName) => {
  const widget = WidgetRegistry.find((w) => w.id === widgetId);
  if (widget && !widget.dashboards.includes(dashboardName)) {
    widget.dashboards.push(dashboardName);
  }
};

export const getWidgetSeedRows = () =>
  WidgetRegistry.map((w) => ({
    name: w.id,
    friendly_name: w.friendly_name,
    category: w.category,
    description: w.description || null,
    is_active: true,
  }));

export const getWidgetCategories = () => [
  "System",
  "User Management",
  "Reporting",
  "ANPR",
  "Jobs",
  "CRM",
  "Messaging",
  "Devices",
  "Opportunities",
  "Documents",
  "Operations",
  "Sales",
];

export async function ensureWidgetCategories(axiosLike = axiosInstance) {
  const cats = getWidgetCategories().map((name) => ({ name, is_active: true }));
  try {
    await axiosLike.post("widgets/categories/registry-sync", cats);
    console.log("✅ Widget categories synced.");
  } catch (e) {
    console.warn(
      "Bulk category registry-sync not available; ensure via SQL migration.",
      e?.response?.status || e?.message
    );
  }
}

export async function syncWidgetsTable(axiosLike = axiosInstance) {
  try {
    await ensureWidgetCategories(axiosLike);
  } catch {}
  const rows = getWidgetSeedRows();

  try {
    await axiosLike.post("widgets/registry-sync", rows);
    console.log("✅ Widgets bulk synced.");
    return;
  } catch (e) {
    console.warn(
      "Bulk widget registry-sync not available; falling back to per-row upsert…",
      e?.response?.status || e?.message
    );
  }

  for (const r of rows) {
    try {
      await axiosLike.post("widgets", r);
    } catch (err) {
      try {
        await axiosLike.put(`widgets/${encodeURIComponent(r.name)}`, r);
      } catch (err2) {
        console.error(
          "❌ Widget upsert failed:",
          r.name,
          err2?.response?.data || err2?.message
        );
      }
    }
  }
  console.log("✅ Widgets per-row sync finished.");
}

export default WidgetRegistry;